import { useNavigate } from 'react-router-dom';
import { AlertTriangle, LogIn, PawPrint, ShoppingBag, Star, Store } from 'lucide-react';
import { AuthImage } from '@/components/auth-image';
import type {
  AdoptionItem,
  BannerItem,
  BookingItem,
  CategoryItem,
  OrderItem,
  PlanItem,
  ProductItem,
  Section,
  VendorItem,
} from './types';

/**
 * The file endpoints live under /api/v1, but the axios client's baseURL already includes
 * it, so image paths from the API are stripped of that prefix before AuthImage refetches
 * them. Mobile clients do the equivalent (prefix their own base URL).
 */
const asset = (p: string) => p.replace(/^\/api\/v1/, '');

/**
 * Which section types render as which rail. Grouping vendor-shaped rails together is the
 * point of the envelope — the client keys off `type`, and ANYTHING not listed here falls
 * through to `null` and is skipped, exactly as an older app build would skip a rail the
 * server added after it shipped.
 */
const VENDOR_RAILS = new Set([
  'EMERGENCY_CLINICS',
  'VETS_NEARBY',
  'CLINICS_NEARBY',
  'LABS_NEARBY',
  'NEARBY_WALKERS',
  'TOP_GROOMERS',
  'CREMATION_SERVICES',
  'RESTAURANTS_NEARBY',
  'CAFES_NEARBY',
  'TOP_RATED_DINING',
  'NEARBY_SERVICES',
  'RECOMMENDED_VENDORS',
  'RECOMMENDED_TRAINERS',
  'TOP_VETS',
  'PET_FRIENDLY_NEARBY',
]);
const CATEGORY_RAILS = new Set(['SERVICE_CATEGORIES', 'HEALTH_CATEGORIES', 'FOOD_CATEGORIES']);
const PRODUCT_RAILS = new Set(['FOOD_PRODUCTS', 'PHARMACY_PRODUCTS']);
const BOOKING_RAILS = new Set([
  'UPCOMING_BOOKINGS',
  'UPCOMING_SERVICE',
  'UPCOMING_APPOINTMENT',
  'RECENT_ACTIVITY',
  'RECENT_HEALTH_ACTIVITY',
]);
const ORDER_RAILS = new Set(['ACTIVE_ORDERS', 'RECENT_ORDERS']);
const ADOPTION_RAILS = new Set(['ADOPTION_NEARBY', 'ADOPTION_SPOTLIGHT']);

function RailHeader({ title, note }: { title: string; note?: string }) {
  return (
    <div className="mb-3 px-1">
      <h2 className="text-lg font-bold tracking-tight text-foreground">{title}</h2>
      {note && (
        <p className="mt-0.5 text-xs text-muted-foreground" data-testid="rail-note">
          {note}
        </p>
      )}
    </div>
  );
}

/** Horizontal, snap-scrolling rail — the carousel shape every tab uses. */
function Rail({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex snap-x gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {children}
    </div>
  );
}

function LoginPrompt({ text }: { text: string }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/user/login')}
      className="flex w-full items-center gap-3 rounded-card border-2 border-dashed border-brand-outline-variant/40 bg-card/50 p-4 text-left text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
    >
      <LogIn className="h-5 w-5 shrink-0 text-primary" />
      {text}
    </button>
  );
}

function BannerCarousel({ items }: { items: BannerItem[] }) {
  return (
    <Rail>
      {items.map((b) => (
        <div
          key={b.id}
          className="relative aspect-[21/9] w-[85%] shrink-0 snap-start overflow-hidden rounded-card sm:w-[420px]"
        >
          <AuthImage
            path={`${asset(b.image_path)}?variant=full`}
            alt={b.title}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/55 to-transparent p-4">
            <span className="text-sm font-semibold text-white">{b.title}</span>
          </div>
        </div>
      ))}
    </Rail>
  );
}

function CategoryChips({ items }: { items: CategoryItem[] }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((c) => {
        const key = c.module ?? c.category ?? c.label;
        return (
          <button
            key={key}
            type="button"
            onClick={() => c.module && navigate(`/app/discover?module=${c.module}`)}
            className="clay-press flex items-center gap-2 rounded-full border-2 border-brand-outline-variant/25 bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-clay transition-all hover:-translate-y-0.5 hover:border-primary/40"
          >
            {c.label}
            {typeof c.count === 'number' && (
              <span className="rounded-full bg-primary/10 px-1.5 text-xs font-bold text-primary">
                {c.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function VendorRail({ items, emergency }: { items: VendorItem[]; emergency?: boolean }) {
  const navigate = useNavigate();
  return (
    <Rail>
      {items.map((v) => (
        <button
          key={v.id}
          type="button"
          onClick={() => navigate(`/app/discover/${v.id}`)}
          data-testid={`tab-vendor-${v.id}`}
          className="clay-press flex w-[230px] shrink-0 snap-start flex-col overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-card text-left shadow-clay transition-all hover:-translate-y-1 hover:border-primary/40"
        >
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
            {v.cover_photo_path ? (
              <AuthImage
                path={`${asset(v.cover_photo_path)}?variant=medium`}
                alt={v.business_name ?? 'Vendor'}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="accent-gradient flex h-full w-full items-center justify-center text-white/90">
                <Store className="h-8 w-8" />
              </div>
            )}
            {emergency && (
              <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white">
                <AlertTriangle className="h-3 w-3" /> 24×7
              </span>
            )}
            {v.review_count > 0 && (
              <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-xs font-semibold text-white backdrop-blur-sm">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                {v.avg_rating.toFixed(1)}
              </span>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-1 p-3">
            <span className="line-clamp-1 font-semibold text-foreground">{v.business_name}</span>
            <span className="text-xs text-muted-foreground">
              {typeof v.distance_km === 'number' ? `${v.distance_km} km away` : (v.city ?? '')}
            </span>
            {v.reason && (
              <span className="mt-1 line-clamp-1 text-xs font-medium text-primary" title={v.reason}>
                {v.reason}
              </span>
            )}
          </div>
        </button>
      ))}
    </Rail>
  );
}

function ProductRail({ items }: { items: ProductItem[] }) {
  return (
    <Rail>
      {items.map((p) => (
        <div
          key={p.id}
          className="flex w-[170px] shrink-0 snap-start flex-col overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay"
        >
          <div className="relative aspect-square w-full overflow-hidden bg-muted">
            {p.images?.[0] ? (
              <AuthImage path={asset(p.images[0].thumb_path)} alt={p.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <ShoppingBag className="h-7 w-7" />
              </div>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-0.5 p-2.5">
            {p.brand && <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{p.brand}</span>}
            <span className="line-clamp-2 text-sm font-medium text-foreground">{p.name}</span>
            {p.price_from_inr && (
              <span className="mt-0.5 text-sm font-bold text-foreground">₹{p.price_from_inr}</span>
            )}
          </div>
        </div>
      ))}
    </Rail>
  );
}

const pill = 'rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide';
const fmt_when = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

function BookingList({ items }: { items: BookingItem[] }) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((b) => (
        <div
          key={b.id}
          className="flex items-center justify-between rounded-card border-2 border-brand-outline-variant/20 bg-card p-3 shadow-clay"
        >
          <div className="flex flex-col">
            <span className="font-semibold text-foreground">{b.module_type}</span>
            <span className="text-xs text-muted-foreground">{fmt_when(b.slot_start)}</span>
          </div>
          <span className={`${pill} bg-primary/10 text-primary`}>{b.status}</span>
        </div>
      ))}
    </div>
  );
}

function OrderList({ items }: { items: OrderItem[] }) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((o) => (
        <div
          key={o.id}
          className="flex items-center justify-between rounded-card border-2 border-brand-outline-variant/20 bg-card p-3 shadow-clay"
        >
          <div className="flex flex-col">
            <span className="font-semibold text-foreground">{o.kind === 'COMMERCE' ? 'Shop order' : 'Dining order'}</span>
            <span className="text-xs text-muted-foreground">{fmt_when(o.created_at)}</span>
          </div>
          <span className={`${pill} bg-secondary text-secondary-foreground`}>{o.status}</span>
        </div>
      ))}
    </div>
  );
}

function PlanList({ items }: { items: PlanItem[] }) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((p) => (
        <div
          key={p.id}
          className="flex items-center justify-between rounded-card border-2 border-brand-outline-variant/20 bg-card p-3 shadow-clay"
        >
          <span className="font-semibold text-foreground">
            {p.kind === 'TRAINING_PACKAGE' ? 'Training package' : 'Walk plan'}
          </span>
          <span className={`${pill} bg-emerald-100 text-emerald-800`}>{p.status}</span>
        </div>
      ))}
    </div>
  );
}

function AdoptionRail({ items }: { items: AdoptionItem[] }) {
  const navigate = useNavigate();
  return (
    <Rail>
      {items.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => navigate(`/app/adopt/${a.id}`)}
          className="clay-press flex w-[190px] shrink-0 snap-start flex-col gap-1 rounded-card border-2 border-brand-outline-variant/20 bg-card p-3 text-left shadow-clay transition-all hover:-translate-y-1 hover:border-primary/40"
        >
          <div className="accent-gradient flex aspect-[16/10] items-center justify-center rounded-lg text-white/90">
            <PawPrint className="h-8 w-8" />
          </div>
          <span className="font-semibold text-foreground">{a.breed || a.species}</span>
          <span className="text-xs text-muted-foreground">
            {typeof a.distance_km === 'number' ? `${a.distance_km} km away` : (a.city ?? '')}
          </span>
        </button>
      ))}
    </Rail>
  );
}

/**
 * Render one section by its `type`. Unknown types return null — the app skips a rail it
 * doesn't understand rather than failing, which is what lets the backend add rails without
 * a client release.
 */
export function SectionRenderer({ section }: { section: Section }) {
  // A personal rail the caller isn't signed in for: render the login prompt in its place.
  if (section.cta?.action === 'LOGIN' && section.items.length === 0) {
    return (
      <section>
        <RailHeader title={section.title} />
        <LoginPrompt text={section.cta.text} />
      </section>
    );
  }
  if (section.items.length === 0) return null;

  let body: React.ReactNode = null;
  if (section.type === 'BANNERS') body = <BannerCarousel items={section.items as BannerItem[]} />;
  else if (CATEGORY_RAILS.has(section.type)) body = <CategoryChips items={section.items as CategoryItem[]} />;
  else if (VENDOR_RAILS.has(section.type))
    body = <VendorRail items={section.items as VendorItem[]} emergency={section.type === 'EMERGENCY_CLINICS'} />;
  else if (PRODUCT_RAILS.has(section.type)) body = <ProductRail items={section.items as ProductItem[]} />;
  else if (BOOKING_RAILS.has(section.type)) body = <BookingList items={section.items as BookingItem[]} />;
  else if (ORDER_RAILS.has(section.type)) body = <OrderList items={section.items as OrderItem[]} />;
  else if (section.type === 'ACTIVE_PLANS') body = <PlanList items={section.items as PlanItem[]} />;
  else if (ADOPTION_RAILS.has(section.type)) body = <AdoptionRail items={section.items as AdoptionItem[]} />;

  if (body === null) return null; // unknown type → skip

  return (
    <section>
      <RailHeader title={section.title} note={section.note} />
      {body}
    </section>
  );
}
